import React, { useState, useRef, useEffect } from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import env from '../../environment.js';

const STORAGE_KEY = 'Sirio-simbologia-colores-recientes';
const MAX_RECIENTES = 5;
const MAX_USADOS = 8;

// Normaliza a '#rrggbb' en minúsculas; devuelve null si no es un hex de 6 dígitos.
export const normalizeHex = color => {
  const c = String(color || '')
    .trim()
    .toLowerCase();
  return /^#[0-9a-f]{6}$/.test(c) ? c : null;
};

// Lee los últimos colores elegidos (más reciente primero). Nunca lanza:
// si el storage no está disponible o el JSON está corrupto devuelve [].
export const getColoresRecientes = () => {
  try {
    const raw = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    if (!Array.isArray(raw)) return [];
    return raw
      .map(normalizeHex)
      .filter(Boolean)
      .slice(0, MAX_RECIENTES);
  } catch (e) {
    return [];
  }
};

// Agrega un color al inicio de la lista (sin duplicados, máx. 5) y devuelve la lista vigente.
export const addColorReciente = color => {
  const hex = normalizeHex(color);
  const actuales = getColoresRecientes();
  if (!hex) return actuales;
  const nuevos = [hex, ...actuales.filter(c => c !== hex)].slice(0, MAX_RECIENTES);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nuevos));
  } catch (e) {
    // Storage no disponible (modo privado, cuota, etc.): se ignora.
  }
  return nuevos;
};

// Arma la lista [{ color, nombres }] de colores distintos ya guardados en el mapa.
// `items` son los elementos de simbología; `esColorUsuario(item)` decide si el color del ítem es una elección del usuario.
export const buildUsedColors = (items, esColorUsuario) => {
  const porColor = new Map();
  (items || []).forEach(item => {
    if (!esColorUsuario(item)) return;
    const hex = normalizeHex(item.color);
    if (!hex) return;
    if (!porColor.has(hex)) porColor.set(hex, []);
    const nombres = porColor.get(hex);
    const nombre = item.nombre || 'Sin nombre';
    if (!nombres.includes(nombre)) nombres.push(nombre);
  });
  return Array.from(porColor, ([color, nombres]) => ({ color, nombres }));
};

const captionStyle = {
  fontSize: 10,
  color: env.colors.neutral_400,
  fontWeight: 600,
  margin: '2px 0 4px',
  display: 'block',
};

// Marca de selección (check blanco con sombra) para swatches de color.
const Check = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth="4"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      filter: 'drop-shadow(0 0 1px rgba(0, 0, 0, 0.8))',
    }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Anillo blanco interior + borde exterior (sutil, o marcado si está seleccionado) para que se vean colores claros.
const swatchShadow = selected =>
  selected ? `inset 0 0 0 2px #fff, 0 0 0 2px ${env.colors.primary_300}` : 'inset 0 0 0 2px #fff, 0 0 0 1px rgba(0, 0, 0, 0.25)';

// Círculo de color de 20px (sin interacción); `selected` agrega anillo y palomita.
const Swatch = ({ hex, selected }) => (
  <span
    aria-hidden="true"
    style={{
      position: 'relative',
      display: 'inline-block',
      width: 20,
      height: 20,
      flexShrink: 0,
      borderRadius: '50%',
      background: hex,
      boxShadow: swatchShadow(selected),
    }}>
    {selected && <Check />}
  </span>
);

const Circulo = ({ hex, title, selected, onClick }) => (
  <button
    type="button"
    title={title}
    aria-label={`${title} ${hex}`}
    aria-pressed={selected}
    onClick={onClick}
    onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.15)')}
    onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
    style={{
      position: 'relative',
      width: 20,
      height: 20,
      padding: 0,
      flexShrink: 0,
      cursor: 'pointer',
      borderRadius: '50%',
      background: hex,
      margin: 2,
      border: 'none',
      boxShadow: swatchShadow(selected),
      transition: 'transform 0.1s ease',
    }}>
    {selected && <Check />}
  </button>
);

// Fila del popover: swatch + texto en una sola línea (elipsis si no cabe).
const Fila = ({ hex, texto, selected, onClick }) => (
  <button
    type="button"
    title={texto}
    aria-label={`${texto} ${hex}`}
    aria-pressed={selected}
    onClick={onClick}
    onMouseEnter={e => (e.currentTarget.style.background = env.colors.neutral_2)}
    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      width: '100%',
      textAlign: 'left',
      background: 'transparent',
      border: 'none',
      borderRadius: 6,
      padding: '4px 6px',
      fontSize: 12,
      color: env.colors.neutral_700,
      cursor: 'pointer',
    }}>
    <Swatch hex={hex} selected={selected} />
    <span
      style={{
        flex: 1,
        minWidth: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
      {texto}
    </span>
  </button>
);

/**
 * Selector de color para simbología: campo con el mismo estilo que los Select del toolbar (label "Color" en el borde)
 * que abre un popover para reutilizar colores en un clic. El campo muestra el color actual y no crece con la cantidad de colores. Dentro del popover:
 * color original del catálogo, colores ya usados en el mapa (con sus nombres), recientes y "Elegir otro color…"
 * (input nativo, siempre montado pero oculto). Si no hay nada que ofrecer, el campo abre el selector nativo directo.
 * - value: color actual (hex)
 * - onChange(hex): se llama igual que el input nativo
 * - usedColors: [{ color, nombres: [] }] colores ya guardados en el mapa
 * - recentColors: [hex] últimos colores elegidos por el usuario
 * - defaultColor: color original del elemento elegido en el catálogo (hex) o null
 * - defaultLabel: nombre del elemento del catálogo, se muestra junto a "Color original" (opcional)
 */
const SimbologiaColorPicker = ({ value, onChange, usedColors = [], recentColors = [], defaultColor = null, defaultLabel = null }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const actual = normalizeHex(value);
  const original = normalizeHex(defaultColor);

  const usados = usedColors
    .map(u => ({ ...u, color: normalizeHex(u.color) }))
    .filter(u => u.color)
    .slice(0, MAX_USADOS);
  // Un color que ya aparece en "En este mapa" o como color original no se repite en "Recientes".
  const hexExcluidos = new Set(usados.map(u => u.color));
  if (original) hexExcluidos.add(original);
  const recientes = [...new Set(recentColors.map(normalizeHex).filter(Boolean))].filter(c => !hexExcluidos.has(c)).slice(0, MAX_RECIENTES);

  const hayOpciones = !!original || usados.length > 0 || recientes.length > 0;

  // Cierra con clic fuera y con Escape (solo mientras está abierto).
  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const abrirNativo = () => {
    const input = inputRef.current;
    if (!input) return;
    if (input.showPicker) {
      try {
        input.showPicker();
        return;
      } catch (e) {
        // Algunos navegadores lo rechazan: se usa click().
      }
    }
    input.click();
  };

  const elegir = hex => {
    onChange(hex);
    setOpen(false);
  };

  const onTriggerClick = () => {
    if (!hayOpciones) {
      abrirNativo();
      return;
    }
    setOpen(o => !o);
  };

  const divider = <div style={{ height: 1, background: env.colors.neutral_100, margin: '6px 0' }} />;

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <div style={{ minWidth: 70 }}>
        <FormControl fullWidth>
          <InputLabel id="simbologiaColor-label" className="pr-1">
            Color
          </InputLabel>
          <Select
            labelId="simbologiaColor-label"
            id="simbologiaColor"
            label="Color"
            value={actual || ''}
            open={false}
            onOpen={onTriggerClick}
            displayEmpty
            inputProps={{ 'aria-label': 'Elegir color' }}
            SelectDisplayProps={{ 'aria-expanded': open, 'aria-haspopup': 'dialog' }}
            style={{ height: 36, minWidth: 70, fontSize: '14px' }}
            className="mt-1"
            renderValue={() => (
              <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Swatch hex={actual || '#ffffff'} selected={false} />
              </span>
            )}>
            <MenuItem value={actual || ''} style={{ display: 'none' }} />
          </Select>
        </FormControl>
      </div>

      <input
        ref={inputRef}
        type="color"
        value={value}
        onChange={e => onChange(e.target.value)}
        tabIndex={-1}
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          opacity: 0,
          width: 1,
          height: 1,
          padding: 0,
          border: 0,
          pointerEvents: 'none',
        }}
      />

      {open && (
        <div
          role="dialog"
          aria-label="Elegir color"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            zIndex: 1000,
            width: 260,
            background: '#fff',
            border: `1px solid ${env.colors.neutral_100}`,
            borderRadius: 8,
            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
            padding: 10,
          }}>
          {original && (
            <div style={{ marginBottom: 6 }}>
              <Fila
                hex={original}
                texto={`Color original${defaultLabel ? ` (${defaultLabel})` : ''}`}
                selected={original === actual}
                onClick={() => elegir(original)}
              />
            </div>
          )}

          {usados.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <span style={captionStyle}>En este mapa</span>
              {usados.map(u => (
                <Fila
                  key={u.color}
                  hex={u.color}
                  texto={u.nombres.join(', ')}
                  selected={u.color === actual}
                  onClick={() => elegir(u.color)}
                />
              ))}
            </div>
          )}

          {recientes.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <span style={captionStyle}>Recientes</span>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 4,
                  padding: '0 4px',
                }}>
                {recientes.map(c => (
                  <Circulo key={c} hex={c} title="Color usado recientemente" selected={c === actual} onClick={() => elegir(c)} />
                ))}
              </div>
            </div>
          )}

          {hayOpciones && divider}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              abrirNativo();
            }}
            onMouseEnter={e => (e.currentTarget.style.background = env.colors.neutral_2)}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              textAlign: 'left',
              background: 'transparent',
              border: 'none',
              borderRadius: 6,
              padding: '4px 6px',
              fontSize: 12,
              color: env.colors.neutral_700,
              cursor: 'pointer',
            }}>
            <i className="fa fa-palette" style={{ width: 20, textAlign: 'center' }} />
            Elegir otro color…
          </button>
        </div>
      )}
    </div>
  );
};

export default SimbologiaColorPicker;
